import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/auth-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const signupSchema = z
  .object({
    email: z.string().email('Email inválido'),
    password: z.string().min(6, 'Mínimo 6 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((input) => input.password === input.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type SignupInput = z.infer<typeof signupSchema>;

export function SignupPage() {
  const signUp = useAuthStore((state) => state.signUp);
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);
  const [emailConfirmationPending, setEmailConfirmationPending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (input: SignupInput) => {
    setAuthError(null);
    setEmailConfirmationPending(false);
    const { error, needsEmailConfirmation } = await signUp(input.email, input.password);
    if (error) {
      setAuthError(error.message);
      return;
    }
    if (needsEmailConfirmation) {
      setEmailConfirmationPending(true);
      return;
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-8">
      <Card className="w-full max-w-md border-border/60">
        <CardHeader>
          <CardTitle
            className="font-brand text-2xl tracking-[0.3em] text-foreground"
            data-testid="signup-title"
          >
            CREAR CUENTA
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Te creás como operador. El admin asigna privilegios después.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-sm text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                data-testid="email-input"
                {...register('email')}
              />
              {errors.email ? (
                <span className="text-xs text-destructive" data-testid="email-error">
                  {errors.email.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm text-muted-foreground">
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                data-testid="password-input"
                {...register('password')}
              />
              {errors.password ? (
                <span className="text-xs text-destructive" data-testid="password-error">
                  {errors.password.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="confirmPassword" className="text-sm text-muted-foreground">
                Repetir password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                data-testid="confirm-password-input"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <span className="text-xs text-destructive" data-testid="confirm-password-error">
                  {errors.confirmPassword.message}
                </span>
              ) : null}
            </div>
            {authError ? (
              <div
                role="alert"
                className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive"
                data-testid="auth-error"
              >
                {authError}
              </div>
            ) : null}
            {emailConfirmationPending ? (
              <output
                className="rounded border border-warning/30 bg-warning/10 p-2 text-sm text-warning"
                data-testid="email-confirmation-pending"
              >
                Cuenta creada. Revisá tu email para confirmar antes de hacer sign-in.
              </output>
            ) : null}
            <Button type="submit" disabled={isSubmitting} data-testid="signup-submit">
              {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Ya tenés cuenta?{' '}
              <Link to="/login" className="text-primary underline" data-testid="link-login">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
